/* eslint-disable react-hooks/static-components */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import classNames from 'classnames';
import root from 'window-or-global';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';

import { Modal } from '@johnlewispartnership/wtr-ingredients/ingredients/Modal';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { TypographySecondary } from '@johnlewispartnership/wtr-ingredients/foundations/typography/TypographySecondary';
import Alert from '@johnlewispartnership/wtr-ingredients/ingredients/Alert';
import {
  Calendar,
  TrolleyInactive,
  SuccessSolid,
  Warning,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { Button } from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/ingredients/LinkAsButton/LinkAsButton';

import urls from 'constants/urls';
import { getFeatureFlags } from 'utils/feature-flags';
import history from 'utils/history';
import locator from 'utils/locator';
import { getCustomerOrderId, isUserLoggedIn } from 'redux/modules/sessions/selectors';
import { hasBookedSlot } from 'redux/modules/customer-slot/selectors';
import { trackNotificationLinkClicked } from 'analytics/notification-tracking';
import { trackPreLoginInteraction } from 'analytics/preLoginInteractionsTracking';
import { redirectToLogin, storeLoginRedirect } from 'utils/login';
import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';

// Only used within useEffect
// eslint-disable-next-line no-restricted-imports
import { getCurrentBreakpoint } from 'utils/mediaQueries';

import { setSlotBookingRedirectPath } from 'redux/modules/slot-booking/actions/set-slot-booking-redirect-path';

import {
  getLineNumbers,
  getNumberOfSelectedShoppableProducts,
  getNumberOfShoppableProducts,
  getShoppableProducts,
} from 'redux/modules/recipes/selectors';
import addAllItems from 'redux/modules/add-all-items/actions/add-all-items';
import { getAddAllItemsStatus } from 'redux/modules/add-all-items/selectors';
import { FULFILLED, PENDING } from 'redux/modules/add-all-items/reducer';
import { getConflictedProducts } from 'redux/modules/trolley-errors/selectors';
import { getProducts } from 'redux/modules/entities/selectors/products';
import { CLEAR_TROLLEY_ERRORS } from 'redux/modules/trolley-errors/actions/types';
import { trackShoppableBanner } from 'analytics/recipes-tracking';
import { createOrderItems } from './utils';
import RecipesProductGrid, { LineNumber, ProductDataType } from '../ProductGrid';
import useIntersection from './use-intersection';
import styles from './index.scss';

import SwapProduct from '../SwapProduct';
import type { SwapProductPayload } from '../SwapProduct';
import SlotButton from './SlotButton';

export interface ShoppableProps {
  title: string;
  recipeId: string;
}

export interface ConflictedProduct {
  message: string;
  ingredient: string;
}

export interface SwapProductHandler {
  payload: SwapProductPayload | null;
}

export interface SwapProductState extends SwapProductHandler {
  isVisible: boolean;
}

const STICKY_BANNER = 'banner';
const FEATURE_CARD = 'feature card';

const ShoppableHeading = ({ title, recipeId }: ShoppableProps) => {
  const { shoppableRecipesMVP: shoppableRecipesMVPEnabled } = getFeatureFlags();
  const dispatch = useWtrDispatch();
  const [showProducts, setShowProducts] = useState(false);
  const isLoggedIn = useWtrSelector(isUserLoggedIn);
  const hasSlot = useWtrSelector(hasBookedSlot);
  const shoppableProducts = useWtrSelector(getShoppableProducts);

  const trolleyStatus = useWtrSelector(getAddAllItemsStatus);
  const [successfulTrolley, setSuccessfulTrolley] = useState(false);

  const conflictedProducts = useWtrSelector(getConflictedProducts);
  const [trolleyConflict, setTrolleyConflict] = useState<ConflictedProduct[]>();

  const [swapProductState, setSwapProductState] = useState<SwapProductState>({
    isVisible: false,
    payload: null,
  });

  const showSwapProduct = ({ payload }: SwapProductHandler) => {
    setSwapProductState({
      isVisible: true,
      payload,
    });
  };

  const closeSwapProduct = () => {
    setSwapProductState({
      isVisible: false,
      payload: null,
    });
  };

  // Product data
  const products = useWtrSelector(getProducts);
  const lineNumbers: LineNumber[] = useWtrSelector(getLineNumbers);

  const productData: ProductDataType[] = useMemo(
    () => lineNumbers.map(line => products[line]).filter(Boolean),
    [products, lineNumbers],
  );

  const getNameByLineNumber = (line: LineNumber) =>
    Object(productData.find(({ lineNumber }) => lineNumber === line)).name;

  const trolleyConflicts: ConflictedProduct[] = conflictedProducts.map(
    ({ message, products: product }) => ({
      message,
      ingredient: getNameByLineNumber(product?.[0].lineNumber),
    }),
  );

  const redirectAfterLogin = () => {
    // Needed to redirect to slots after login
    trackPreLoginInteraction('Sign in To Shop');
    storeLoginRedirect(urls.serviceSelection);
    locator.href = urls.login;
  };

  const bookASlot = () => {
    // Needed to return user to recipe after login/book a slot
    dispatch(setSlotBookingRedirectPath(`${locator.pathname}?shoppableProducts`));

    if (!isLoggedIn) {
      redirectAfterLogin();
    }
    if (isLoggedIn) {
      history.push(urls.serviceSelection);
    }

    trackNotificationLinkClicked('shoppable', 'book a slot');
  };

  const numOfProducts = useWtrSelector(getNumberOfShoppableProducts);
  const totalSelected = useWtrSelector(getNumberOfSelectedShoppableProducts);

  const [setNode, entry] = useIntersection({ rootMargin: '10px' });

  const shopBannerRef = useRef(null);
  const swapProductIsVisibleRef = useRef(swapProductState.isVisible);

  useEffect(() => {
    swapProductIsVisibleRef.current = swapProductState.isVisible;
  }, [swapProductState.isVisible]);

  useEffect(() => {
    setNode(shopBannerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onBackButton = () => {
    if (swapProductIsVisibleRef.current) {
      closeSwapProduct();
    } else {
      history.replace(root.location.pathname + root.location.search);
      setShowProducts(false);
    }
  };

  const openModal = (type: string) => {
    if (!isLoggedIn) {
      redirectToLogin();
    } else {
      if (getCurrentBreakpoint() === 'xs') {
        root.addEventListener('popstate', onBackButton);
        history.push(`${root.location.pathname + root.location.search}#shop`);
      }
      setShowProducts(true);

      if (shoppableProducts && shoppableProducts.length > 0) {
        trackShoppableBanner(type, recipeId, shoppableProducts);
      }
    }
  };

  const closeModal = () => {
    if (getCurrentBreakpoint() === 'xs') {
      root.removeEventListener('popstate', onBackButton);
      history.replace(root.location.pathname + root.location.search);
    }

    if (showProducts) {
      dispatch({
        type: CLEAR_TROLLEY_ERRORS,
      });
    }

    setShowProducts(false);
  };

  const addIngredientsToTrolley = () => {
    if (!isLoggedIn) {
      storeLoginRedirect(urls.serviceSelection);
      locator.href = urls.login;

      return;
    }

    const orderId = getCustomerOrderId();
    const orderItems = createOrderItems(shoppableProducts!); // eslint-disable-line @typescript-eslint/no-non-null-assertion

    dispatch(
      addAllItems({
        merge: true,
        orderId,
        orderItems,
      }),
    );
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTrolleyConflict(trolleyConflicts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflictedProducts]);

  // Handles trolley success/fail screens
  useEffect(() => {
    if (trolleyStatus === FULFILLED) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSuccessfulTrolley(true);
      setTimeout(() => {
        setSuccessfulTrolley(false);
        setShowProducts(trolleyConflicts.length > 0);
      }, 1500);
    }
  }, [trolleyConflicts.length, trolleyStatus]);

  const shoppableProductsNotAvailable = !!(shoppableProducts && shoppableProducts.length === 0);

  const ShopBanner = ({ showBookSlotBtn, type }: { showBookSlotBtn: boolean; type: string }) => {
    return (
      <div className={classNames(styles.shopBanner, styles.hideBtnIcon)}>
        {showBookSlotBtn && <SlotButton testId="book-slot" shortTime />}
        <Button
          theme="finalising"
          onClick={() => openModal(type)}
          startIcon={<TrolleyInactive />}
          width="fit"
          data-testid="shop-ingredients-button"
          waiting={shoppableProductsNotAvailable}
          aria-disabled={shoppableProductsNotAvailable}
          aria-label={shoppableProductsNotAvailable ? 'Loading Ingredients' : 'Shop ingredients'}
        >
          Shop ingredients
        </Button>
      </div>
    );
  };

  const isTrolleyPending = trolleyStatus === PENDING;

  const isTrolleySuccessWithConflict =
    successfulTrolley && trolleyConflict && trolleyConflict.length > 0;

  const isTrolleySuccessNoConflict =
    successfulTrolley && trolleyConflict && !trolleyConflict.length;

  const defaultModalState = !successfulTrolley && trolleyStatus !== PENDING;

  const [currentIngredientIndex, setCurrentIngredientIndex] = useState(0);
  const [currentSwappedProduct, setCurrentSwappedProduct] = useState<SwapProductPayload | null>(
    null,
  );

  return shoppableRecipesMVPEnabled ? (
    <section className={classNames(styles.content, 'no-print')} data-testid="shoppable">
      {!hasSlot && (
        <div className={styles.bookSlot}>
          <Alert
            type="info"
            title="Book a slot to see product availability at your nearest Waitrose & Partners store"
            actions={
              <LinkAsButton
                data-testid="book-slot"
                className={styles.bookSlotBtn}
                onClick={bookASlot}
                theme="secondary"
                startIcon={<Calendar />}
                width="fit"
                label="Book a slot"
              />
            }
          />
        </div>
      )}
      <div ref={shopBannerRef} className={styles.staticBanner}>
        <ShopBanner showBookSlotBtn={hasSlot} type={FEATURE_CARD} />
      </div>
      <div
        className={classNames(styles.sticky, {
          [styles.showShopCard]: !entry.isIntersecting,
        })}
      >
        <div className="container-fluid">
          <ShopBanner showBookSlotBtn type={STICKY_BANNER} />
        </div>
      </div>
      <Modal
        titleText="Products dialogue"
        className={styles.modal}
        contentClassName={styles['modal-content']}
        isOpen={showProducts}
        handleModalClose={closeModal}
        cannotClose={isTrolleyPending || successfulTrolley || swapProductState.isVisible}
      >
        {isTrolleyPending && (
          <div className={styles.loadingWrapper} data-testid="loading-modal">
            <Spinner ariaTextInactive="Loading finished" isActive={isTrolleyPending} size="large" />
            <Typography
              element="span"
              styleAs="sectionHeading"
              aria-live="polite"
              aria-atomic="true"
            >
              Adding ingredients to your trolley
            </Typography>
          </div>
        )}

        {isTrolleySuccessWithConflict && (
          <div className={styles.successWrapper} data-testid="success-modal">
            <Warning className={styles.warningIcon} size="large" />
            <Typography
              element="span"
              styleAs="sectionHeading"
              aria-live="polite"
              aria-atomic="true"
            >
              We&apos;re sorry, some items couldn&apos;t be added to your trolley.
            </Typography>
          </div>
        )}
        {isTrolleySuccessNoConflict && (
          <div className={styles.successWrapper} data-testid="success-modal">
            <SuccessSolid className={styles.successIcon} size="large" />
            <Typography
              element="span"
              styleAs="sectionHeading"
              aria-live="polite"
              aria-atomic="true"
            >
              Ingredients added to your trolley
            </Typography>
          </div>
        )}

        {swapProductState.isVisible && (
          <SwapProduct
            payload={swapProductState?.payload ?? null}
            closeSwapProduct={closeSwapProduct}
            recipeId={recipeId}
            shoppableProductIndex={currentIngredientIndex}
            setCurrentSwappedProduct={setCurrentSwappedProduct}
          />
        )}

        {!swapProductState.isVisible && defaultModalState && (
          <>
            <div className={styles.modalHeader} data-testid="ingredients-modal">
              <h2>
                <Typography element="span" styleAs="paragraph">
                  Ingredients you need to make...
                </Typography>
                <TypographySecondary component="span" font="sebenta" size={16}>
                  {title}
                </TypographySecondary>
              </h2>
              <div className={styles.selectedIngredients}>
                {totalSelected}/{numOfProducts} Selected Ingredients
              </div>
            </div>
            <div className={styles.alerts}>
              <Alert type="info" title="Please check quantities">
                Some of the ingredients below may not show the correct amount needed, please adjust
                to suit
              </Alert>
              {trolleyConflict &&
                trolleyConflict.length > 0 &&
                trolleyConflict.map(({ message, ingredient }) => {
                  return (
                    <div
                      key={ingredient}
                      className={styles.trolleyConflict}
                      data-testid="trolley-conflict"
                    >
                      <Alert type="error" title={ingredient}>
                        {message}
                      </Alert>
                    </div>
                  );
                })}
            </div>
            <RecipesProductGrid
              recipeId={recipeId}
              showSwapProduct={showSwapProduct}
              setCurrentIngredientIndex={setCurrentIngredientIndex}
              currentSwappedProduct={currentSwappedProduct}
            />
            <div className={styles.buttonWrapper}>
              <Button
                className={styles.button}
                theme="finalising"
                startIcon={<TrolleyInactive />}
                onClick={addIngredientsToTrolley}
                disabled={totalSelected === 0}
              >
                Add selected items to trolley
              </Button>
            </div>
          </>
        )}
      </Modal>
    </section>
  ) : null;
};

export default ShoppableHeading;
